import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {GlowText} from "../components/dimus/GlowText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {PulseRing} from "../components/dimus/PulseRing";
import {ScanLine} from "../components/dimus/ScanLine";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * O QUE EU APRENDI ERRANDO #04 — "Eu ignorei os números por 3 meses"
 * Editing Style: CINEMATIC
 * Profile: @guilhermeribeiro.ai
 * Audio: ElevenLabs TTS per-scene (voz Guilherme)
 * Evolução vs #03: Dashboard animado, Counter, fases internas, StaggerList
 */

const TEAL = "#2dd4bf";
const TEAL_DARK = "#052020";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const RED = "#F87171";
const AMBER = "#f5ad43";
const GREEN = "#00DF82";

const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 156, file: "audio/aprendi04/aprendi04_c01.mp3", dir: "right" as const},
  {dur: 356, file: "audio/aprendi04/aprendi04_c02.mp3", dir: "left" as const},
  {dur: 458, file: "audio/aprendi04/aprendi04_c03.mp3", dir: "up" as const},
  {dur: 352, file: "audio/aprendi04/aprendi04_c04.mp3", dir: "right" as const},
  {dur: 128, file: "audio/aprendi04/aprendi04_c05.mp3", dir: "up" as const},
];

export const APRENDI04_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

// ─── Particles ────────────────────────────────────────────────────────
const Particles: React.FC<{count: number; color: string; falling?: boolean}> = ({count, color, falling}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`px4-${i}`) * 1080;
        const baseY = random(`py4-${i}`) * 1920;
        const size = 2 + random(`ps4-${i}`) * 3;
        const speed = 0.2 + random(`psp4-${i}`) * 0.4;
        const dir = falling ? 1 : -1;
        const wrapped = ((baseY + frame * speed * dir) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.08 + random(`po4-${i}`) * 0.12,
          }} />
        );
      })}
    </>
  );
};

// ─── Dashboard Card ───────────────────────────────────────────────────
const DashCard: React.FC<{
  label: string; value: string; color: string; delay: number;
  blurred?: boolean; shake?: boolean; border?: string;
}> = ({label, value, color, delay, blurred, shake, border}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  const shakeX = shake && frame > delay + 30
    ? Math.sin(frame * 0.6) * 3 * interpolate(frame - delay - 30, [0, 60], [1, 0], CLAMP)
    : 0;

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateY(${interpolate(p, [0, 1], [30, 0], CLAMP)}px) translateX(${shakeX}px)`,
      padding: "20px 28px", borderRadius: 16, width: "85%",
      background: "rgba(255,255,255,0.04)",
      border: border || `1px solid rgba(255,255,255,0.08)`,
      filter: blurred ? "blur(8px)" : "none",
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK}}>
        {label}
      </div>
      <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color, fontFamily: FONT_STACK}}>
        {value}
      </div>
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const hookP = spring({frame: frame - 60, fps, config: SPRINGS.heavy});

  return (
    <OrganicLayer intensity="subtle" seed="aprendi-04">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 30}}>
      <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
      <PulseRing delay={0} color={TEAL} size={300} />
      <GradientText text="APRENDI #04" colors={[TEAL, AMBER]} fontSize={FONT_SCALE.h1.max} springProfile="bouncy" />
      <div style={{
        opacity: hookP,
        transform: `translateY(${interpolate(hookP, [0, 1], [20, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3,
        maxWidth: "88%",
      }}>
        3 meses sem olhar um número.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 2: O MEDO — 3 fases ───────────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // FASE B (120-240): Dashboard desfocado
  const dashP = spring({frame: frame - 130, fps, config: SPRINGS.smooth});
  // FASE C (240+): "tá funcionando"
  const fakeP = spring({frame: frame - 250, fps, config: SPRINGS.smooth});
  const dashExit = frame >= 240
    ? interpolate(frame, [240, 270], [1, 0], CLAMP)
    : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />

      {/* FASE A: MEDO */}
      {frame < 140 && (
        <GlowText text="MEDO" delay={10} size={FONT_SCALE.hero.min} color={RED} />
      )}

      {/* FASE B: Dashboard blurrado */}
      {frame >= 120 && frame < 280 && (
        <div style={{
          opacity: dashP * dashExit, display: "flex", flexDirection: "column",
          gap: 14, alignItems: "center", width: "100%",
        }}>
          <ScanLine color={TEAL} />
          <DashCard label="Faturamento" value="R$ 42.000" color={GREEN} delay={140} blurred />
          <DashCard label="Custos" value="R$ ???" color={AMBER} delay={155} blurred />
          <DashCard label="Lucro real" value="R$ ???" color={WHITE} delay={170} blurred />
        </div>
      )}

      {/* FASE C: "tá funcionando" */}
      {frame >= 240 && (
        <div style={{
          opacity: fakeP,
          transform: `scale(${interpolate(fakeP, [0, 1], [0.95, 1], CLAMP)})`,
          fontSize: FONT_SCALE.h2.min, fontWeight: 600, color: MUTED,
          fontFamily: FONT_STACK, textAlign: "center", fontStyle: "italic",
        }}>
          "Tá funcionando."
        </div>
      )}
    </AbsoluteFill>
  );
};

// ─── SCENE 3: A DESCOBERTA — Dashboard real + Counter ─────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // Fase A: Dashboard com números reais
  const explodeAt = 260;
  const isExploding = frame >= explodeAt;

  // Counter animado para custo (sobe de 180 a 420)
  const costProgress = interpolate(frame, [30, 120], [0, 1], CLAMP);
  const costValue = Math.round(180 + (420 - 180) * costProgress);

  // Shake no card de resultado negativo
  const resultDelay = 150;
  const resultP = spring({frame: frame - resultDelay, fps, config: SPRINGS.heavy});
  const resultShake = frame > resultDelay + 30
    ? Math.sin(frame * 0.8) * 4 * interpolate(frame - resultDelay - 30, [0, 80], [1, 0], CLAMP)
    : 0;

  // Texto grande "PREJUÍZO DISFARÇADO"
  const bigTextP = spring({frame: frame - explodeAt - 10, fps, config: SPRINGS.heavy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />

      {/* Dashboard fase A */}
      {!isExploding && (
        <div style={{
          display: "flex", flexDirection: "column", gap: 14,
          alignItems: "center", width: "100%",
        }}>
          {/* Card: Custo por cliente — Counter sobe */}
          <DashCard
            label="Custo por cliente"
            value={`R$ ${costValue}`}
            color={costValue > 350 ? RED : AMBER}
            delay={10}
            shake={costValue > 380}
            border={costValue > 350 ? `1px solid ${RED}40` : undefined}
          />
          {/* Card: Ticket médio — estável */}
          <DashCard label="Ticket médio" value="R$ 350" color={AMBER} delay={60} />
          {/* Card: Resultado — negativo */}
          {frame >= resultDelay && (
            <div style={{
              opacity: resultP,
              transform: `translateY(${interpolate(resultP, [0, 1], [30, 0], CLAMP)}px) translateX(${resultShake}px)`,
              padding: "20px 28px", borderRadius: 16, width: "85%",
              background: "rgba(248,113,113,0.08)",
              border: `2px solid ${RED}60`,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK}}>
                Resultado por cliente
              </div>
              <div style={{fontSize: FONT_SCALE.h1.min, fontWeight: 900, color: RED, fontFamily: FONT_STACK}}>
                -R$ 70
              </div>
              <div style={{
                height: 3, width: "60%", background: RED, borderRadius: 2,
                opacity: 0.5 + Math.sin(frame * 0.1) * 0.3,
              }} />
            </div>
          )}
        </div>
      )}

      {/* Fase B: PREJUÍZO DISFARÇADO */}
      {isExploding && (
        <div style={{
          opacity: bigTextP,
          transform: `scale(${interpolate(bigTextP, [0, 1], [0.7, 1], CLAMP)})`,
          textAlign: "center",
        }}>
          <GradientText text="PREJUÍZO" colors={[RED, AMBER]} fontSize={FONT_SCALE.hero.min} springProfile="heavy" />
          <div style={{
            opacity: spring({frame: frame - explodeAt - 30, fps, config: SPRINGS.smooth}),
            fontSize: FONT_SCALE.h2.min, fontWeight: 600, color: MUTED,
            fontFamily: FONT_STACK, marginTop: 8,
          }}>
            disfarçado de faturamento.
          </div>
        </div>
      )}

      <Particles count={20} color={RED} falling />
    </AbsoluteFill>
  );
};

// ─── SCENE 4: A LIÇÃO — StaggerList ritmado ──────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const lines = [
    {text: "Número não mente.", color: WHITE, delay: 10},
    {text: "Número não tem ego.", color: WHITE, delay: 60},
    {text: "Número não quer te agradar.", color: WHITE, delay: 110},
    {text: "Ele cresce no escuro.", color: RED, delay: 170, glow: true},
  ];

  // Crescimento visual: círculo escuro que expande na frase final
  const darkGrowP = frame > 200
    ? interpolate(frame, [200, 352], [0, 1], CLAMP)
    : 0;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left + 20}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />

      {/* Círculo escuro crescente como metáfora */}
      <div style={{
        position: "absolute", left: "50%", top: "50%",
        width: 600 * darkGrowP, height: 600 * darkGrowP,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(248,113,113,0.06) 0%, transparent 70%)",
        transform: "translate(-50%, -50%)",
      }} />

      {lines.map((line, i) => {
        const p = spring({frame: frame - line.delay, fps, config: SPRINGS.heavy});
        if (frame < line.delay) return null;
        return (
          <div key={i} style={{
            opacity: p,
            transform: `translateX(${interpolate(p, [0, 1], [-40, 0], CLAMP)}px)`,
          }}>
            {line.glow ? (
              <GlowText text={line.text} delay={line.delay} size={FONT_SCALE.h2.max} color={line.color} />
            ) : (
              <div style={{
                fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: line.color,
                fontFamily: FONT_STACK, lineHeight: 1.2,
              }}>
                {line.text}
              </div>
            )}
          </div>
        );
      })}
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA ────────────────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 8, fps, config: SPRINGS.snappy});
  const textP = spring({frame: frame - 20, fps, config: SPRINGS.heavy});
  const btnP = spring({frame: frame - 50, fps, config: SPRINGS.bouncy});
  const pulse = frame > 70 ? 1 + Math.sin((frame - 70) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 24, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />

      <div style={{
        opacity: textP,
        transform: `translateY(${interpolate(textP, [0, 1], [15, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: TEAL,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2,
      }}>
        Abre ele hoje.
      </div>

      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${TEAL}`, boxShadow: `0 0 20px rgba(45,212,191,0.3)`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Bastidores reais</div>
        </div>
      </div>

      <div style={{
        opacity: btnP,
        transform: `translateY(${interpolate(btnP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(btnP, [0, 1], [0.5, 1], CLAMP) * pulse})`,
        padding: "18px 52px", borderRadius: 999, background: TEAL,
        fontSize: FONT_SCALE.cta.max, fontWeight: 700, color: "#000", fontFamily: FONT_STACK,
        boxShadow: `0 0 40px rgba(45,212,191,0.4)`,
      }}>
        SEGUE PRA MAIS
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE FADE ──────────────────────────────────────────────────────
const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const Aprendi04: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, ${TEAL_DARK}, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={25} color={TEAL} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
